import { useFocusEffect } from '@react-navigation/native';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import ConfirmDialog from '../../components/ConfirmDialog';

export type FormBackHandler = () => Promise<boolean> | boolean;

interface FormNavigationApi {
  requestBack: () => Promise<boolean>;
  confirmLeave: () => Promise<boolean>;
  setHandler: (handler: FormBackHandler) => void;
  clearHandler: (handler: FormBackHandler) => void;
  cancelConfirm: () => void;
}

const FormNavigationContext = createContext<FormNavigationApi | undefined>(
  undefined
);

const noHandler = async () => false;
const alwaysLeave = async () => true;

export const FormNavigationProvider: React.FC<PropsWithChildren> = ({
  children
}) => {
  const handlerRef = useRef<FormBackHandler | undefined>(undefined);
  const resolveRef = useRef<((leave: boolean) => void) | undefined>(undefined);
  const pendingRef = useRef<Promise<boolean> | undefined>(undefined);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const settleRef = useRef<((leave: boolean) => void) | undefined>(undefined);
  const api = useMemo<FormNavigationApi>(
    () => ({
      setHandler: handler => {
        handlerRef.current = handler;
      },
      clearHandler: handler => {
        if (handlerRef.current === handler) handlerRef.current = undefined;
      },
      cancelConfirm: () => settleRef.current?.(false),
      requestBack: async () =>
        handlerRef.current ? Boolean(await handlerRef.current()) : false,
      confirmLeave: () => {
        if (pendingRef.current) return pendingRef.current;
        pendingRef.current = new Promise<boolean>(resolve => {
          resolveRef.current = resolve;
          setConfirmVisible(true);
        });
        return pendingRef.current;
      }
    }),
    []
  );
  const settle = (leave: boolean) => {
    if (!resolveRef.current) return;
    setConfirmVisible(false);
    const resolve = resolveRef.current;
    resolveRef.current = undefined;
    pendingRef.current = undefined;
    resolve?.(leave);
  };
  settleRef.current = settle;
  return (
    <FormNavigationContext.Provider value={api}>
      {children}
      <ConfirmDialog
        visible={confirmVisible}
        onRequestClose={() => settle(false)}
        title='Leave this form?'
        message='Answers on this page have not been saved.'
        confirmLabel='Leave'
        cancelLabel='Stay'
        onConfirmButtonClicked={() => settle(true)}
        onCancelButtonClicked={() => settle(false)}
      />
    </FormNavigationContext.Provider>
  );
};

export const useFormNavigation = () => {
  const api = useContext(FormNavigationContext);
  return {
    requestBack: api?.requestBack ?? noHandler,
    confirmLeave: api?.confirmLeave ?? alwaysLeave
  };
};

export const useRegisterFormBack = (handler: FormBackHandler | undefined) => {
  const api = useContext(FormNavigationContext);
  const handlerRef = useRef(handler);
  useLayoutEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  useFocusEffect(
    useCallback(() => {
      const registered: FormBackHandler = () => handlerRef.current?.() ?? false;
      api?.setHandler(registered);
      return () => {
        api?.clearHandler(registered);
        api?.cancelConfirm();
      };
    }, [api])
  );
};
